/**
 * Hook para monitoramento da fila de campanhas
 * 
 * Fornece estatísticas, itens da fila, volume por hora e alertas
 * Similar ao useQueueMonitor mas focado em campaign_recipients
 * 
 * @version 1.0 - Monitoramento Admin de Campanhas
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';

// Interface para estatísticas gerais da fila de campanhas
export interface CampaignQueueStats {
  pending_count: number;
  queued_count: number;
  processing_count: number;
  sending_count: number;
  sent_count: number;
  delivered_count: number;
  read_count: number;
  failed_count: number;
  skipped_count: number;
  success_rate: number;
  avg_processing_time_ms: number;
}

// Interface para item individual da fila
export interface CampaignQueueItem {
  id: string;
  campaign_id: string;
  campaign_name: string;
  tenant_id: string;
  tenant_name: string;
  inbox_id: string | null;
  inbox_name: string | null;
  provider_slug: string | null;
  phone: string;
  contact_name: string | null;
  status: string;
  error_message: string | null;
  variables: Record<string, unknown> | null;
  created_at: string;
  processing_started_at: string | null;
  sent_at: string | null;
  delivered_at: string | null;
  read_at: string | null;
}

// Interface para volume por hora
export interface CampaignQueueVolume {
  hour: string;
  sent: number;
  failed: number;
}

// Interface para alertas de saúde
export interface CampaignQueueAlert {
  type: 'warning' | 'error' | 'info';
  title: string;
  message: string;
  count?: number;
}

/**
 * Hook para buscar estatísticas gerais da fila de campanhas
 */
export function useCampaignQueueStats() {
  return useQuery({
    queryKey: ['campaign-queue-stats-admin'],
    queryFn: async (): Promise<CampaignQueueStats> => {
      const now = new Date();
      const last24h = new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString();
      
      // Buscar contagens por status em paralelo
      const [pendingRes, queuedRes, processingRes, sendingRes, sentRes, deliveredRes, readRes, failedRes, skippedRes, avgTimeRes] = await Promise.all([
        supabase.from('campaign_recipients').select('id', { count: 'exact', head: true }).eq('status', 'pending'),
        supabase.from('campaign_recipients').select('id', { count: 'exact', head: true }).eq('status', 'queued'),
        supabase.from('campaign_recipients').select('id', { count: 'exact', head: true }).eq('status', 'processing'),
        supabase.from('campaign_recipients').select('id', { count: 'exact', head: true }).eq('status', 'sending'),
        supabase.from('campaign_recipients').select('id', { count: 'exact', head: true }).eq('status', 'sent').gte('sent_at', last24h),
        supabase.from('campaign_recipients').select('id', { count: 'exact', head: true }).eq('status', 'delivered').gte('delivered_at', last24h),
        supabase.from('campaign_recipients').select('id', { count: 'exact', head: true }).eq('status', 'read').gte('read_at', last24h),
        supabase.from('campaign_recipients').select('id', { count: 'exact', head: true }).eq('status', 'failed').gte('created_at', last24h),
        supabase.from('campaign_recipients').select('id', { count: 'exact', head: true }).eq('status', 'skipped').gte('created_at', last24h),
        // Tempo médio de processamento (sent_at - processing_started_at)
        supabase.from('campaign_recipients')
          .select('processing_started_at, sent_at')
          .eq('status', 'sent')
          .not('processing_started_at', 'is', null)
          .not('sent_at', 'is', null)
          .gte('sent_at', last24h)
          .limit(100)
      ]);
      
      const pending = pendingRes.count || 0;
      const queued = queuedRes.count || 0;
      const processing = processingRes.count || 0;
      const sending = sendingRes.count || 0;
      const sent = sentRes.count || 0;
      const delivered = deliveredRes.count || 0;
      const read = readRes.count || 0;
      const failed = failedRes.count || 0;
      const skipped = skippedRes.count || 0;
      
      // Calcular tempo médio de processamento
      let avgTime = 0;
      if (avgTimeRes.data && avgTimeRes.data.length > 0) {
        const times = avgTimeRes.data
          .filter(r => r.processing_started_at && r.sent_at)
          .map(r => new Date(r.sent_at!).getTime() - new Date(r.processing_started_at!).getTime());
        if (times.length > 0) {
          avgTime = times.reduce((a, b) => a + b, 0) / times.length;
        }
      }
      
      // Taxa de sucesso
      const totalProcessed = sent + delivered + read + failed;
      const successRate = totalProcessed > 0 ? ((sent + delivered + read) / totalProcessed) * 100 : 100;
      
      return {
        pending_count: pending,
        queued_count: queued,
        processing_count: processing,
        sending_count: sending,
        sent_count: sent,
        delivered_count: delivered,
        read_count: read,
        failed_count: failed,
        skipped_count: skipped,
        success_rate: Math.round(successRate * 10) / 10,
        avg_processing_time_ms: Math.round(avgTime)
      };
    },
    refetchInterval: 10000,
    staleTime: 5000
  });
}

/**
 * Hook para buscar itens da fila de campanhas com enriquecimento
 */
export function useCampaignQueueItems(
  statusFilter: string = 'all',
  providerFilter: string = 'all',
  limit: number = 500
) {
  return useQuery({
    queryKey: ['campaign-queue-items-admin', statusFilter, providerFilter, limit],
    queryFn: async (): Promise<CampaignQueueItem[]> => {
      // Query base com joins
      let query = supabase
        .from('campaign_recipients')
        .select(`
          id,
          campaign_id,
          phone,
          contact_name,
          status,
          error_message,
          variables,
          created_at,
          processing_started_at,
          sent_at,
          delivered_at,
          read_at,
          sent_via_inbox_id,
          campaigns!inner(name, tenant_id)
        `)
        .order('created_at', { ascending: false })
        .limit(limit);
      
      // Aplicar filtro de status
      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter as any);
      }
      
      const { data: recipients, error } = await query;
      
      if (error) {
        logger.error('[useCampaignQueueItems] Erro ao buscar recipients:', error.message);
        throw error;
      }
      
      if (!recipients || recipients.length === 0) {
        return [];
      }
      
      // Extrair IDs únicos para queries batch
      const tenantIds = [...new Set(recipients.map(r => (r.campaigns as any)?.tenant_id).filter(Boolean))];
      const inboxIds = [...new Set(recipients.map(r => r.sent_via_inbox_id).filter(Boolean))];
      
      // Buscar tenants e inboxes em paralelo
      const [tenantsRes, inboxesRes] = await Promise.all([
        tenantIds.length > 0 
          ? supabase.from('tenants').select('id, name').in('id', tenantIds)
          : Promise.resolve({ data: [] }),
        inboxIds.length > 0
          ? supabase.from('inboxes').select('id, name, provider_id').in('id', inboxIds as string[])
          : Promise.resolve({ data: [] })
      ]);
      
      // Buscar providers para inboxes
      const providerIds = [...new Set((inboxesRes.data || []).map(i => i.provider_id).filter(Boolean))];
      const providersRes = providerIds.length > 0
        ? await supabase.from('api_providers').select('id, slug').in('id', providerIds)
        : { data: [] };
      
      // Criar mapas para lookup O(1)
      const tenantsMap = new Map((tenantsRes.data || []).map(t => [t.id, t.name]));
      const providersMap = new Map((providersRes.data || []).map(p => [p.id, p.slug]));
      const inboxesMap = new Map((inboxesRes.data || []).map(i => [i.id, {
        name: i.name,
        provider_slug: providersMap.get(i.provider_id) || null
      }]));
      
      // Enriquecer recipients
      const enriched: CampaignQueueItem[] = recipients.map(r => {
        const campaign = r.campaigns as any;
        const inboxInfo = r.sent_via_inbox_id ? inboxesMap.get(r.sent_via_inbox_id) : null;
        
        return {
          id: r.id,
          campaign_id: r.campaign_id,
          campaign_name: campaign?.name || 'Desconhecida',
          tenant_id: campaign?.tenant_id || '',
          tenant_name: campaign?.tenant_id ? tenantsMap.get(campaign.tenant_id) || 'Desconhecido' : 'Desconhecido',
          inbox_id: r.sent_via_inbox_id,
          inbox_name: inboxInfo?.name || null,
          provider_slug: inboxInfo?.provider_slug || null,
          phone: r.phone,
          contact_name: r.contact_name,
          status: r.status || 'pending',
          error_message: r.error_message,
          variables: r.variables as Record<string, unknown> | null,
          created_at: r.created_at || '',
          processing_started_at: r.processing_started_at,
          sent_at: r.sent_at,
          delivered_at: r.delivered_at,
          read_at: r.read_at
        };
      });
      
      // Filtrar por provider se necessário
      if (providerFilter !== 'all') {
        return enriched.filter(item => item.provider_slug === providerFilter);
      }
      
      return enriched;
    },
    refetchInterval: 15000,
    staleTime: 5000
  });
}

/**
 * Hook para buscar volume por hora
 */
export function useCampaignQueueVolume(hours: number = 24) {
  return useQuery({
    queryKey: ['campaign-queue-volume-admin', hours],
    queryFn: async (): Promise<CampaignQueueVolume[]> => {
      const now = new Date();
      const startTime = new Date(now.getTime() - hours * 60 * 60 * 1000);
      
      // Buscar enviados e falhas nas últimas X horas
      const [sentRes, failedRes] = await Promise.all([
        supabase
          .from('campaign_recipients')
          .select('sent_at')
          .in('status', ['sent', 'delivered', 'read'])
          .gte('sent_at', startTime.toISOString())
          .order('sent_at', { ascending: true })
          .limit(5000),
        supabase
          .from('campaign_recipients')
          .select('created_at')
          .eq('status', 'failed')
          .gte('created_at', startTime.toISOString())
          .order('created_at', { ascending: true })
          .limit(2000)
      ]);
      
      // Agregar por hora
      const volumeMap = new Map<string, { sent: number; failed: number }>();
      
      // Inicializar todas as horas
      for (let i = 0; i < hours; i++) {
        const hourDate = new Date(now.getTime() - (hours - 1 - i) * 60 * 60 * 1000);
        const hourKey = hourDate.toISOString().slice(0, 13);
        volumeMap.set(hourKey, { sent: 0, failed: 0 });
      }
      
      // Contar enviados
      (sentRes.data || []).forEach(r => {
        if (r.sent_at) {
          const hourKey = r.sent_at.slice(0, 13);
          const existing = volumeMap.get(hourKey);
          if (existing) {
            existing.sent++;
          }
        }
      });
      
      // Contar falhas
      (failedRes.data || []).forEach(r => {
        if (r.created_at) {
          const hourKey = r.created_at.slice(0, 13);
          const existing = volumeMap.get(hourKey);
          if (existing) {
            existing.failed++;
          }
        }
      });
      
      // Converter para array
      return Array.from(volumeMap.entries()).map(([hour, data]) => ({
        hour: new Date(hour + ':00:00Z').toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
        sent: data.sent,
        failed: data.failed
      }));
    },
    refetchInterval: 60000,
    staleTime: 30000
  });
}

/**
 * Hook para buscar alertas de saúde da fila
 */
export function useCampaignQueueAlerts() {
  return useQuery({
    queryKey: ['campaign-queue-alerts-admin'],
    queryFn: async (): Promise<CampaignQueueAlert[]> => {
      const alerts: CampaignQueueAlert[] = [];
      const now = new Date();
      const fiveMinAgo = new Date(now.getTime() - 5 * 60 * 1000).toISOString();
      const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000).toISOString();
      
      // Verificar recipients stuck em processing
      const { count: stuckCount } = await supabase
        .from('campaign_recipients')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'processing')
        .lt('processing_started_at', fiveMinAgo);
      
      if (stuckCount && stuckCount > 0) {
        alerts.push({
          type: 'error',
          title: 'Recipients Travados',
          message: `${stuckCount} recipients estão em processamento há mais de 5 minutos`,
          count: stuckCount
        });
      }
      
      // Verificar taxa de falha alta
      const [sentCount, failedCount] = await Promise.all([
        supabase.from('campaign_recipients').select('id', { count: 'exact', head: true }).in('status', ['sent', 'delivered', 'read']).gte('sent_at', oneHourAgo),
        supabase.from('campaign_recipients').select('id', { count: 'exact', head: true }).eq('status', 'failed').gte('created_at', oneHourAgo)
      ]);
      
      const totalRecent = (sentCount.count || 0) + (failedCount.count || 0);
      if (totalRecent > 10) {
        const failRate = ((failedCount.count || 0) / totalRecent) * 100;
        if (failRate > 10) {
          alerts.push({
            type: 'warning',
            title: 'Taxa de Falha Alta',
            message: `${failRate.toFixed(1)}% de falhas na última hora`,
            count: failedCount.count || 0
          });
        }
      }
      
      // Verificar fila PGMQ crescendo (via pending/queued)
      const { count: queuedCount } = await supabase
        .from('campaign_recipients')
        .select('id', { count: 'exact', head: true })
        .in('status', ['pending', 'queued']);
      
      if (queuedCount && queuedCount > 500) {
        alerts.push({
          type: 'warning',
          title: 'Fila Grande',
          message: `${queuedCount} recipients aguardando processamento`,
          count: queuedCount
        });
      }
      
      return alerts;
    },
    refetchInterval: 30000,
    staleTime: 15000
  });
}

/**
 * Hook para buscar logs de campanha de um recipient
 */
export function useCampaignRecipientLogs(recipientId: string | null) {
  return useQuery({
    queryKey: ['campaign-recipient-logs', recipientId],
    queryFn: async () => {
      if (!recipientId) return [];
      
      const { data, error } = await supabase
        .from('campaign_logs')
        .select('*')
        .eq('recipient_id', recipientId)
        .order('created_at', { ascending: true })
        .limit(50);
      
      if (error) {
        logger.error('[useCampaignRecipientLogs] Erro:', error.message);
        throw error;
      }
      
      return data || [];
    },
    enabled: !!recipientId,
    staleTime: 10000
  });
}
